import { fetchAuthSession, getCurrentUser } from '@aws-amplify/auth';
import { log as _log } from './ChatStreamLogger';

function getRetryDelayMs(retryNumber) {
  return Math.min(1000 * Math.pow(2, retryNumber - 1), 10000);
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function emitDiagnostic(onStreamDiagnostic, info) {
  if (typeof onStreamDiagnostic === 'function') {
    try {
      onStreamDiagnostic({ ...info, timestamp: Date.now() });
    } catch (_) {}
  }
}

async function consumeJsonSse(response, onEvent, onStreamDiagnostic) {
  const reader = response.body?.getReader();
  if (!reader) {
    _log("SSE", "consumeJsonSse: no reader available on response body");
    return;
  }

  const decoder = new TextDecoder();
  let buffer = '';
  let eventName = null;
  let eventId = null;
  let dataLines = [];
  let _dispatchCount = 0;

  const dispatchEvent = () => {
    if (dataLines.length === 0) return;
    const rawData = dataLines.join('\n');
    dataLines = [];
    try {
      const parsed = JSON.parse(rawData);
      _dispatchCount++;
      onEvent(eventName, parsed, eventId);
    } catch {
      _log("SSE", `PARSE ERROR event=${eventName} raw=${rawData.slice(0, 200)}`);
      emitDiagnostic(onStreamDiagnostic, { event: 'parse_error', raw: rawData.slice(0, 200) });
    }
    eventName = null;
    eventId = null;
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      // Flush decoder and force a terminating blank line for a final dispatch.
      buffer += decoder.decode();
      buffer += '\n\n';
    } else {
      buffer += decoder.decode(value, { stream: true });
    }
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      const normalizedLine = line.endsWith('\r') ? line.slice(0, -1) : line;
      if (normalizedLine.startsWith('event:')) {
        eventName = normalizedLine.slice(6).trim();
      } else if (normalizedLine.startsWith('data:')) {
        dataLines.push(normalizedLine.slice(5));
      } else if (normalizedLine.startsWith('id:')) {
        eventId = normalizedLine.slice(3).trim();
      } else if (normalizedLine === '') {
        dispatchEvent();
      }
    }

    if (done) break;
  }
}

function emitConnectionState(onConnectionStateChange, state) {
  if (typeof onConnectionStateChange === 'function') {
    onConnectionStateChange(state);
  }
}

export async function streamJsonSseWithRetry({
  url,
  abortController,
  onEvent,
  shouldReconnect,
  onConnectionStateChange,
  onStreamDiagnostic,
  getReconnectUrl,
  maxRetries = 5,
  logLabel = 'SSE stream',
  skipAuth = false,
}) {
  let retries = 0;
  let lastEventId = null;
  let activeUrl = url;

  while (retries <= maxRetries) {
    try {
      const connState = retries === 0 ? 'connecting' : 'reconnecting';
      emitConnectionState(onConnectionStateChange, connState);
      _log("SSE", `[${logLabel}] ${connState} to ${activeUrl} (retry ${retries}/${maxRetries})`);
      const headers = {
        Accept: 'text/event-stream',
      };
      if (!skipAuth) {
        const { tokens } = await fetchAuthSession();
        const token = tokens.idToken.toString();
        headers.Authorization = `Bearer ${token}`;
        try {
          const user = await getCurrentUser();
          if (user?.username) {
            headers['user-id'] = user.username;
          }
        } catch {
          // Keep stream auth functional even if user lookup is unavailable.
        }
      }

      const response = await fetch(activeUrl, {
        headers,
        signal: abortController.signal,
      });
      if (response.status === 204) {
        emitConnectionState(onConnectionStateChange, 'idle');
        return { aborted: false, connectionFailed: false, noContent: true };
      }

      if (!response.ok) {
        throw new Error(`SSE connection failed: ${response.status}`);
      }

      retries = 0;
      emitConnectionState(onConnectionStateChange, 'connected');
      emitDiagnostic(onStreamDiagnostic, { event: 'connect' });

      await consumeJsonSse(
        response,
        (eventName, parsed, eventId) => {
          if (eventId) {
            const numericId = Number(eventId);
            if (Number.isFinite(numericId)) lastEventId = numericId;
          }
          onEvent(eventName, parsed, eventId);
        },
        onStreamDiagnostic
      );
      const willReconnect = shouldReconnect && shouldReconnect();

      if (willReconnect) {
        retries++;
        if (typeof getReconnectUrl === 'function' && lastEventId) {
          const reconnectUrl = getReconnectUrl(lastEventId);
          if (reconnectUrl) activeUrl = reconnectUrl;
        }
        emitConnectionState(onConnectionStateChange, 'reconnecting');
        emitDiagnostic(onStreamDiagnostic, { event: 'reconnect', retry: retries });
        await sleep(getRetryDelayMs(retries));
        continue;
      }

      emitConnectionState(onConnectionStateChange, 'idle');
      return { aborted: false, connectionFailed: false };
    } catch (e) {
      if (e?.name === 'AbortError' || abortController.signal.aborted) {
        emitConnectionState(onConnectionStateChange, 'idle');
        return { aborted: true, connectionFailed: false };
      }

      retries++;
      if (retries > maxRetries) {
        emitConnectionState(onConnectionStateChange, 'disconnected');
        emitDiagnostic(onStreamDiagnostic, { event: 'disconnect', reason: 'max_retries' });
        console.warn(`${logLabel} gave up after max retries:`, e);
        return { aborted: false, connectionFailed: true };
      }

      if (typeof getReconnectUrl === 'function' && lastEventId) {
        const reconnectUrl = getReconnectUrl(lastEventId);
        if (reconnectUrl) activeUrl = reconnectUrl;
      }

      emitConnectionState(onConnectionStateChange, 'reconnecting');
      const delay = getRetryDelayMs(retries);
      console.warn(`${logLabel} error, retrying in ${delay}ms (${retries}/${maxRetries}):`, e);
      await sleep(delay);
    }
  }

  emitConnectionState(onConnectionStateChange, 'disconnected');
  return { aborted: false, connectionFailed: true };
}
